import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/States";
import { useInvoices, usePackingLists } from "@/api/hooks";
import { formatDate } from "@/domain/datetime";
import { formatQty } from "@/domain/money";

export type DnRefType = "packing" | "invoice";

/**
 * Step 1 of the delivery-note flow: pick the packing list or invoice the
 * shipment is raised from.
 *
 * The tab lives here (as the labelled `refType` select) but the STATE lives in
 * the caller, so the page can keep it after a create fails and so the two list
 * queries stay cached across a tab switch.
 */
export function SourceDocPicker({
  refType,
  onRefTypeChange,
  onSelect,
  busy = false,
}: {
  refType: DnRefType;
  onRefTypeChange: (v: DnRefType) => void;
  onSelect: (id: string) => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const query = q.trim() || undefined;
  const packing = usePackingLists({ q: query, page, pageSize: 25 });
  const invoices = useInvoices({ q: query, page, pageSize: 25 });

  const isPacking = refType === "packing";
  const active = isPacking ? packing : invoices;
  const packingRows = packing.data?.data ?? [];
  const invoiceRows = invoices.data?.data ?? [];
  const count = isPacking ? packingRows.length : invoiceRows.length;

  // A click that is already in flight must not fire a second POST — every POST
  // persists a real delivery note and burns a document number.
  const pick = (id: string) => {
    if (busy) return;
    onSelect(id);
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <FieldLabel htmlFor="dn-ref-type">
            {t("deliveryNotes.refType")}
          </FieldLabel>
          <Select
            id="dn-ref-type"
            containerClassName="w-full"
            value={refType}
            disabled={busy}
            onChange={(e) => {
              setPage(1);
              onRefTypeChange(e.target.value as DnRefType);
            }}
          >
            <option value="packing">{t("deliveryNotes.fromPacking")}</option>
            <option value="invoice">{t("deliveryNotes.fromInvoice")}</option>
          </Select>
        </div>
        <div className="min-w-0 flex-1">
          <FieldLabel htmlFor="dn-ref-search">
            {t("deliveryNotes.selectReference")}
          </FieldLabel>
          <Input
            id="dn-ref-search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            leading={<Search />}
            placeholder={t("common.search")}
            className="w-full max-w-sm"
          />
        </div>
        <span className="ml-auto pb-2 font-mono tabular text-xs text-ink-faint">
          {count} / {active.data?.total ?? 0}
        </span>
      </div>

      <div className="mt-3">
        {active.isLoading ? (
          <LoadingState />
        ) : active.error ? (
          <ErrorState
            message={
              isPacking ? t("packingLists.errorLoad") : t("invoices.errorLoad")
            }
            onRetry={() => void active.refetch()}
          />
        ) : count === 0 ? (
          <EmptyState
            title={
              query
                ? t("common.noMatches")
                : isPacking
                  ? t("packingLists.empty")
                  : t("deliveryNotes.errorNoReference")
            }
          />
        ) : (
          <TableShell
            className={
              (busy ? "pointer-events-none opacity-60 " : "") +
              "max-h-80 overflow-auto"
            }
          >
            <thead>
              {isPacking ? (
                <tr>
                  <Th pin>{t("packingLists.listNo")}</Th>
                  <Th>{t("pi.piNo")}</Th>
                  <Th>{t("common.date")}</Th>
                  <Th>{t("common.shipTo")}</Th>
                  <Th align="center">{t("common.items")}</Th>
                </tr>
              ) : (
                <tr>
                  <Th pin>{t("invoices.invoiceNo")}</Th>
                  <Th>{t("invoices.piNo")}</Th>
                  <Th>{t("common.date")}</Th>
                  <Th>{t("pi.billTo")}</Th>
                  <Th align="center">{t("common.items")}</Th>
                </tr>
              )}
            </thead>
            <tbody>
              {isPacking
                ? packingRows.map((r) => (
                    <Tr key={r.id} interactive>
                      <Td
                        pin
                        className="font-mono tabular font-medium text-brand"
                      >
                        <button
                          type="button"
                          disabled={busy || active.isFetching}
                          onClick={() => pick(r.id)}
                        >
                          {r.plNo}
                        </button>
                      </Td>
                      <Td className="font-mono tabular text-xs text-ink-soft">
                        {r.piNo ?? "—"}
                      </Td>
                      <Td className="font-mono tabular text-xs text-ink-soft">
                        {formatDate(r.date)}
                      </Td>
                      <Td className="text-ink">{r.shipTo ?? "—"}</Td>
                      <Td
                        align="center"
                        className="font-mono tabular text-ink-soft"
                      >
                        {formatQty(r.itemCount)}
                      </Td>
                    </Tr>
                  ))
                : invoiceRows.map((r) => (
                    <Tr key={r.id} interactive>
                      <Td
                        pin
                        className="font-mono tabular font-medium text-brand"
                      >
                        <button
                          type="button"
                          disabled={busy || active.isFetching}
                          onClick={() => pick(r.id)}
                        >
                          {r.invNo}
                        </button>
                      </Td>
                      <Td className="font-mono tabular text-xs text-ink-soft">
                        {r.piNo ?? "—"}
                      </Td>
                      <Td className="font-mono tabular text-xs text-ink-soft">
                        {formatDate(r.date)}
                      </Td>
                      <Td className="text-ink">{r.billToName ?? "—"}</Td>
                      <Td
                        align="center"
                        className="font-mono tabular text-ink-soft"
                      >
                        {formatQty(r.itemCount)}
                      </Td>
                    </Tr>
                  ))}
            </tbody>
          </TableShell>
        )}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button
          variant="secondary"
          disabled={page <= 1 || active.isFetching}
          onClick={() => setPage((p) => p - 1)}
        >
          {t("pagination.previous")}
        </Button>
        <span className="font-mono text-xs">{page}</span>
        <Button
          variant="secondary"
          disabled={page * 25 >= (active.data?.total ?? 0) || active.isFetching}
          onClick={() => setPage((p) => p + 1)}
        >
          {t("pagination.next")}
        </Button>
      </div>
    </div>
  );
}
